import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { CKEditorComponent } from '@ckeditor/ckeditor5-angular';
import * as ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Actions, Client } from 'src/app/models';
import { EmailFooter, WorkflowActivityEmailFooter } from 'src/app/models/email-footer';
import { ValidateDomainResponse } from 'src/app/models/validate-domain-response';
import { 
  ClientService, 
  SecurityService, 
  SystemService, 
  Utilities, 
  WorkflowContextService,
} from 'src/app/services';
import { CkeditorUploadService } from 'src/app/services/ckeditor-upload.service';

@Component({
  selector: 'wm-settings-email',
  standalone: false,
  templateUrl: './settings-email.component.html',
  styleUrls: ['./settings-email.component.scss'],
})
export class SettingsEmailComponent implements OnInit, OnDestroy {
  @ViewChild('htmlEditor', { static: false }) htmlEditor: CKEditorComponent;
  
  showFooterView = true;
  showNewInline = false;
  showUsageModal = false;
  isEditMode = false;
  isSaving = false;

  isLoadingEmailFooters = false;
  
  client: Client;
  clientSub: Subscription;
  routerSub$: Subscription;
  emailFooters: EmailFooter[] = [];
  editingFooter: EmailFooter | null = null;
  
  customFromDomainResult: ValidateDomainResponse;
  isCustomFromTestingEnabled: boolean = false;

  workflowActivities: WorkflowActivityEmailFooter[] = [];
  loadingWorkflowActivities = false;

  canManageEmailFooters = false;

  form: FormGroup;

  public Editor = (ClassicEditor as any);
  public editorConfig = {
    toolbar: [
      'heading', '|', 'bold', 'italic', 'link', '|',
      'bulletedList', 'numberedList', 'imageUpload', '|',
      'undo', 'redo'
    ]
  };

  constructor(
    private fb: FormBuilder, 
    private _uploadAdapter: CkeditorUploadService,
    private _context: WorkflowContextService,
    private _clientSvc: ClientService,
    private _systemSvc: SystemService,
    private _securitySvc: SecurityService,
    private _toastr: ToastrService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.form = this.fb.group({
      label: ['', Validators.required],
      enabled: [true],
      content: ['', Validators.required]
    });
  }

  ngOnInit(): void {
    this._securitySvc
      .isLoginEntitled(Actions.MANAGE_EMAIL_FOOTERS)
      .subscribe(r => this.canManageEmailFooters = r);

    let clientObs = this._context.client 
      ? this._clientSvc.getClient(this._context.client.id) 
      : this._context.client$;

    this.clientSub = clientObs.subscribe(c => {
      this.client = c;
      if (this.client) {
        this.loadEmailFooters();
        this.checkDomainTestStatus();
      }
    });

    this.syncUrl();
    this.routerSub$ = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.syncUrl();
    });
  }

  ngOnDestroy() {
    if (this.clientSub) {
      this.clientSub.unsubscribe();
    }
    if (this.routerSub$) {
      this.routerSub$.unsubscribe();
    }
  }

  syncUrl() {
    const urlStr = this.router.url;
    if (urlStr.includes('/email-footers')) {
      this.showFooterView = true;
    } else if (urlStr.includes('/email-settings')) {
      this.showFooterView = false;
    }
  }

  setView(v: boolean) {
    if (v) {
      this.router.navigate(['../email-footers'], { relativeTo: this.route });
    } else {
      this.router.navigate(['../email-settings'], { relativeTo: this.route });
    }
  }

  loadEmailFooters() {
    if (!this.client || !this.client.id) {
      this.emailFooters = [];
      return;
    }

    this.isLoadingEmailFooters = true;

    this._clientSvc.getEmailFooters(this.client.id).subscribe({
      next: (footers) => {
        this.emailFooters = (footers || []).sort((a, b) => (a.label > b.label ? 1 : -1));
        this.isLoadingEmailFooters = false;
      },
      error: (err) => {
        console.error('Failed to load email footers', err);
        this.emailFooters = [];
        this.isLoadingEmailFooters = false;
      }
    });
  }

  openNewInline() {
    this.isEditMode = false;
    this.editingFooter = new EmailFooter({
      id: Utilities.generateId(),
      clientId: this.client.id,
      label: '',
      htmlContent: '',
      isEnabled: true
    });

    this.form.reset({ label: '', enabled: true, content: '' });
    this.showNewInline = true;
    this.form.markAsPristine();
    this.form.markAsUntouched();

    setTimeout(() => {
      const el = document.querySelector('.new-footer-inline') as HTMLElement | null;
      if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 10);
  }

  openEditInline(footer: EmailFooter) {
    this.isEditMode = true;
    this.editingFooter = JSON.parse(JSON.stringify(footer)); 
    this.form.patchValue({ 
      label: this.editingFooter.label, 
      enabled: this.editingFooter.isEnabled, 
      content: this.editingFooter.htmlContent || '' 
    });
    this.showNewInline = true;
    this.form.markAsPristine();
    this.form.markAsUntouched();
  }

  closeNewInline() {
    this.showNewInline = false;
    this.isEditMode = false;
    this.editingFooter = null;
    this.form.reset();
  }

  saveInline() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.isSaving = true;
    const value = this.form.value;

    this.editingFooter.label = (value.label || '').trim();
    this.editingFooter.isEnabled = !!value.enabled;
    this.editingFooter.htmlContent = value.content || '';

    this._systemSvc.saveEmailFooter(this.editingFooter).subscribe({
      next: (savedEmailFooter) => {
        const existingIndex = this.emailFooters.findIndex(ef => ef.id === savedEmailFooter.id);
        if (existingIndex > -1) {
          savedEmailFooter.inUse = this.emailFooters[existingIndex].inUse;
          this.emailFooters[existingIndex] = savedEmailFooter;
        } else {
          this.emailFooters.push(savedEmailFooter);
        }
        this.emailFooters = this.emailFooters.sort((a, b) => (a.label > b.label ? 1 : -1));
        this._toastr.success('Email Footer successfully saved.');
        this.isSaving = false;
        this.closeNewInline();
      },
      error: (err) => {
        this.isSaving = false;
        this._toastr.error('Error saving email footer.');
      }
    });
  }

  toggleEnabled(footer: EmailFooter) {
    const footerToUpdate = {...footer};
    footerToUpdate.isEnabled = !footerToUpdate.isEnabled;
    this._systemSvc.saveEmailFooter(footerToUpdate).subscribe(saved => {
      const idx = this.emailFooters.findIndex(f => f.id === saved.id);
      if (idx > -1) {
        this.emailFooters[idx] = saved;
      }
      this._toastr.success(`Footer ${saved.isEnabled ? 'enabled' : 'disabled'}.`);
    });
  }

  viewUsage(footerId: string) {
    this.loadingWorkflowActivities = true;
    this.workflowActivities = [];
    this.showUsageModal = true; 

    this._systemSvc.getEmailFooterActivities(footerId).subscribe(activities => {
      if (activities && activities.length > 0) {
        this.workflowActivities = activities;
      }
      this.loadingWorkflowActivities = false;
    }, () => {
      this.loadingWorkflowActivities = false;
    });
  }

  closeUsageModal() {
    this.showUsageModal = false;
    this.workflowActivities = [];
  }

  saveDomainSettings() {
    return this._clientSvc.saveEmailSettings({
      clientId: this.client.id,
      customFromDomain: this.client.customFromDomain
    });
  }

  onSaveDomainClick() {
    this.saveDomainSettings().subscribe(() => {
      this._toastr.success('Email settings saved.');
    });
  }

checkDomainTestStatus() {
  this._securitySvc.isSystemAdministrator().subscribe(isSystemAdmin  => {

    if (!isSystemAdmin ) {
      return;
    }

    this._clientSvc.isDomainTestValidationEnabled(this.client.id).subscribe(result => {
      this.isCustomFromTestingEnabled = result;
    });
  });
}

enableDomainForTesting() {
    if (!this.client.customFromDomain) return;
    this._clientSvc.enableTestModeForCustomFromDomain(this.client.id).subscribe(() => {
      this.isCustomFromTestingEnabled = true;
      this.customFromDomainResult = null;
      this._toastr.success('Domain enabled for testing.');
    });
  }

  disableDomainForTesting() {
    if (!this.client.customFromDomain) return;
    this._clientSvc.disableTestModeForCustomFromDomain(this.client.id).subscribe(() => {
      this.isCustomFromTestingEnabled = false;
      this.customFromDomainResult = null;
      this._toastr.info('Domain disabled for testing.');
    });
  }

  validateEmail() {
    if (!this.client.customFromDomain) return;
    this.saveDomainSettings().subscribe(() => {
      this._clientSvc.validateCustomFromDomain(this.client.id).subscribe(result => {
        this.customFromDomainResult = result;
      });
    });
  }

  public editorReady(editorInstance) {
    const parentId = this.editingFooter ? this.editingFooter.id : ('temp-' + Utilities.generateId());
    editorInstance.plugins.get('FileRepository').createUploadAdapter = (loader) => {
      this._uploadAdapter.setLoader(loader, parentId);
      this._uploadAdapter.loader = loader;
      return this._uploadAdapter;
    };
  } 
}